import { AsyncPipe } from '@angular/common';
import { afterNextRender, ChangeDetectionStrategy, Component, ElementRef, Injector, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest, EMPTY, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, exhaustMap, filter, map, startWith, switchMap, tap, catchError } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { ToastrService } from 'ngx-toastr';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import {
  NimbusMaterialConfirmPopupComponent,
  NimbusMaterialConfirmPopupType,
} from '@nimbus-library/features/shared/components/material/modals/nimbus-material-confirm-popup/nimbus-material-confirm-popup.component';
import { UIModule } from '@nimbus-global/features/ui/ui.module';
import { SharedUIModule } from '@nimbus-library/features/shared/shared.ui.module';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { ConversationSummaryModel } from '@nimbus-library/features/companion-chat/models/companion-conversation.model';
import { NEW_CONVERSATION_ID } from '@nimbus-library/features/companion-chat/companion-chat.routes';
import { CompanionService } from '@nimbus-library/features/companion-chat/services/companion.service';
import { CompanionChatService } from '@nimbus-library/features/companion-chat/services/companion-chat.service';

const SEARCH_DEBOUNCE_TIME_MS = 350;

class CompanionChatListState {
  public constructor(
    public pageSize: number = 10,
    public skip: string | null = null,
    public search: string = '',
    public refresh: boolean = false,
  ) {}
}

@UntilDestroy()
@Component({
  standalone: true,
  selector: 'nimbus-companion-chat-history',
  templateUrl: './companion-chat-history.component.html',
  styleUrls: ['./companion-chat-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    TranslateModule,
    ReactiveFormsModule,
    UIModule,
    SharedUIModule,
    InfiniteScrollDirective,
    MatMenu,
    MatMenuContent,
    MatMenuItem,
    MatMenuTrigger,
  ],
})
export class CompanionChatHistoryComponent implements OnInit {
  @ViewChild('historyList', { static: true })
  public infiniteScroll: ElementRef<HTMLElement>;

  @ViewChild('renameInput')
  public renameInput: ElementRef<HTMLInputElement>;

  private readonly loadTrigger$ = new Subject<CompanionChatListState>();
  private listState = new CompanionChatListState();
  private ratio = 0;

  public readonly conversations$ = new BehaviorSubject<ConversationSummaryModel[]>([]);
  public readonly loading$ = new BehaviorSubject<boolean>(false);
  public readonly error$ = new BehaviorSubject<boolean>(false);
  public readonly selectedConversationId$ = new BehaviorSubject<string | null>(null);
  public readonly isScrollVisible$ = new BehaviorSubject<boolean>(false);

  public readonly ICONS = ICONS;
  public readonly TranslationKeys = TranslationKeys;

  public searchControl = new FormControl<string>('', { nonNullable: true });
  public renameControl = new FormControl<string>('', { nonNullable: true });
  public readonly editingConversationId$ = new BehaviorSubject<string | null>(null);
  public isCollapsed = false;

  public constructor(
    private readonly translateService: TranslateService,
    private readonly companionService: CompanionService,
    private readonly companionChatService: CompanionChatService,
    private readonly dialogService: MatDialog,
    private readonly toastrService: ToastrService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly injector: Injector,
  ) {}

  public ngOnInit(): void {
    this.route.paramMap
      .pipe(
        map((params) => params.get('id') ?? null),
        untilDestroyed(this),
      )
      .subscribe((id) => {
        const conversationId = id === NEW_CONVERSATION_ID ? null : id;
        this.selectedConversationId$.next(conversationId);
        if (conversationId && !this.conversations$.getValue().some((c) => c.conversationId === conversationId)) {
          this.loadTrigger$.next({ ...this.listState, skip: null, refresh: true });
        }
      });

    this.initialize();
    this.bindControls();

    this.companionService.conversationUpdated$
      .pipe(untilDestroyed(this))
      .subscribe(() => this.loadTrigger$.next({ ...this.listState, skip: null, refresh: true }));
  }

  public onResized(): void {
    this.updateScrollState();
  }

  public onCollapsedChange(isCollapsed: boolean): void {
    this.isCollapsed = isCollapsed;
  }

  public selectConversation(conversationId: string | null): void {
    const target = conversationId ?? NEW_CONVERSATION_ID;
    this.router.navigate([target], { relativeTo: this.route.parent });
  }

  public loadMore(): void {
    if (!this.listState.skip) {
      return;
    }
    this.loadTrigger$.next({ ...this.listState });
  }

  public retryLoad(): void {
    this.loadConversations();
  }

  public getConversationDateLabel(updatedAt: string): string {
    return this.companionChatService.formatDate(updatedAt);
  }

  public deleteConversation(conversation?: ConversationSummaryModel): void {
    const isSingle = !!conversation;
    const titleKey = isSingle
      ? TranslationKeys.CompanionChatPageHistoryDeleteConversationConfirmTitle
      : TranslationKeys.CompanionChatPageHistoryClearHistoryConfirmTitle;
    const bodyKey = isSingle
      ? TranslationKeys.CompanionChatPageHistoryDeleteConversationConfirmBody
      : TranslationKeys.CompanionChatPageHistoryClearHistoryConfirmBody;

    this.dialogService
      .open(NimbusMaterialConfirmPopupComponent, {
        data: {
          popupType: NimbusMaterialConfirmPopupType.DELETE,
          title: this.translateService.instant(titleKey),
          body: this.translateService.instant(bodyKey),
        },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.companionService.deleteConversation(conversation?.conversationId)),
        untilDestroyed(this),
      )
      .subscribe({
        next: () => {
          if (!isSingle) {
            this.selectConversation(null);
          }
          if (isSingle && this.selectedConversationId$.getValue() === conversation.conversationId) {
            this.selectConversation(null);
          }
          if (isSingle) {
            this.removeFromConversations(conversation.conversationId);
          } else {
            this.clearConversations();
            this.listState.skip = null;
          }

          this.toastrService.success(this.translateService.instant(TranslationKeys.GeneralSuccessful));
        },
        error: () => {
          this.toastrService.error(this.translateService.instant(TranslationKeys.GeneralError));
        },
      });
  }

  public renameConversation(conversation: ConversationSummaryModel): void {
    this.renameControl.setValue(conversation.title);
    this.editingConversationId$.next(conversation.conversationId);
    afterNextRender(
      () => {
        this.renameInput?.nativeElement.focus();
        this.renameInput?.nativeElement.select();
      },
      { injector: this.injector },
    );
  }

  public isRenameDisabled(conversation: ConversationSummaryModel): boolean {
    const value = this.renameControl.value?.trim() ?? '';
    return !value || value === conversation.title.trim();
  }

  public confirmRename(conversation: ConversationSummaryModel): void {
    const newTitle = this.renameControl.value?.trim();
    if (!newTitle || newTitle === conversation.title.trim()) {
      this.cancelRename();
      return;
    }

    this.companionService
      .patchConversationTitle(conversation.conversationId, newTitle)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.updateConversationTitle(conversation.conversationId, newTitle);
          this.toastrService.success(this.translateService.instant(TranslationKeys.CompanionChatPageHistoryRenameConversationSuccess));
          this.editingConversationId$.next(null);
        },
        error: () => {
          this.toastrService.error(this.translateService.instant(TranslationKeys.CompanionChatPageHistoryRenameConversationError));
        },
      });
  }

  public cancelRename(): void {
    this.editingConversationId$.next(null);
  }

  private initialize(): void {
    this.loadTrigger$
      .pipe(
        exhaustMap(({ skip, search, pageSize, refresh }) => {
          const isFirstPage = skip === null;
          this.error$.next(false);

          if (isFirstPage && !refresh) {
            this.clearConversations();
            this.loading$.next(true);
          }

          return this.companionService.getConversations(pageSize, skip ?? undefined, search || undefined).pipe(
            tap((response) => {
              if (refresh) {
                this.mergeConversations(response.conversations);
              } else {
                this.appendConversations(response.conversations);
                this.listState.skip = response.continuationToken ?? null;
                this.loading$.next(false);
                setTimeout(() => this.updateScrollState(isFirstPage));
              }
            }),
            catchError(() => {
              this.error$.next(true);
              this.loading$.next(false);
              return EMPTY;
            }),
          );
        }),
        untilDestroyed(this),
      )
      .subscribe();

    this.loadTrigger$.next({ ...this.listState });
  }

  private bindControls(): void {
    this.searchControl.valueChanges
      .pipe(
        map((value: string) => (value ?? '').trim().toLowerCase()),
        debounceTime(SEARCH_DEBOUNCE_TIME_MS),
        distinctUntilChanged(),
        untilDestroyed(this),
      )
      .subscribe((value) => {
        this.listState.search = value;
        this.loadConversations();
      });

    combineLatest([this.conversations$, this.searchControl.valueChanges.pipe(startWith(this.searchControl.value))])
      .pipe(untilDestroyed(this))
      .subscribe(([conversations, searchValue]) => {
        const shouldDisable = conversations.length === 0 && !searchValue;
        if (shouldDisable && !this.searchControl.disabled) {
          this.searchControl.disable({ emitEvent: false });
        } else if (!shouldDisable && this.searchControl.disabled) {
          this.searchControl.enable({ emitEvent: false });
        }
      });
  }

  private loadConversations(): void {
    this.listState.skip = null;
    this.loadTrigger$.next({ ...this.listState });
  }

  private mergeConversations(incoming: ConversationSummaryModel[]): void {
    const existing = this.conversations$.getValue();
    const incomingIds = new Set(incoming.map((c) => c.conversationId));
    const preserved = existing.filter((c) => !incomingIds.has(c.conversationId));
    this.conversations$.next(this.sortConversationsByNewest([...incoming, ...preserved]));
  }

  private clearConversations(): void {
    this.conversations$.next([]);
  }

  private appendConversations(incoming: ConversationSummaryModel[]): void {
    this.conversations$.next(this.sortConversationsByNewest([...this.conversations$.getValue(), ...incoming]));
  }

  private removeFromConversations(conversationId: string): void {
    this.conversations$.next(this.conversations$.getValue().filter((c) => c.conversationId !== conversationId));
  }

  private updateConversationTitle(conversationId: string, newTitle: string): void {
    this.conversations$.next(
      this.conversations$.getValue().map((c) => (c.conversationId === conversationId ? { ...c, title: newTitle } : c)),
    );
  }

  private updateScrollState(isInitialLoad = false): void {
    const el = this.infiniteScroll?.nativeElement;
    if (!el) {
      return;
    }

    if (isInitialLoad) {
      // scroll top when conversations loaded initially
      el.scrollTop = 0;
    }

    this.ratio =
      el.scrollHeight !== 0
        ? // eslint-disable-next-line @typescript-eslint/no-magic-numbers
          100 * (el.clientHeight / el.scrollHeight)
        : 0;

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    this.isScrollVisible$.next(!this.listState.skip && this.ratio > 98);

    if (this.listState.skip && el.scrollHeight <= el.clientHeight) {
      this.loadTrigger$.next({ ...this.listState });
    }
  }

  private sortConversationsByNewest(conversations: ConversationSummaryModel[]): ConversationSummaryModel[] {
    return [...conversations].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }
}
